/**
 * SourceDataLoaderService
 *
 * Service for loading all source data types required for DataReferenceTreeWrapper.
 * Centralizes data loading logic, error handling, and detailed logging.
 *
 * This service is designed to be reusable across multiple components that need
 * to display or work with source data (audiences, tasks, study cases, social proofs).
 *
 * Related: Sprint 11, Task T044
 */

import { AudienceService } from '@modules/firebase-core/services/AudienceService';
import { TaskService } from '@modules/firebase-core/services/TaskService';
import { StudyCaseService } from '@modules/firebase-core/services/StudyCaseService';
import { ReviewService } from '@modules/firebase-core/services/ReviewService';
import { RatingService } from '@modules/firebase-core/services/RatingService';
import { BigNumberService } from '@modules/firebase-core/services/BigNumberService';
import type { StudyCase, Review, Rating, BigNumber } from '@segmently/schema-models/entities';
import type { Audience, Actor, Task } from '@segmently/schema-models/entities';

/**
 * Result of loading all source data
 */
export interface SourceDataLoadResult {
  audiences: Audience[];
  actorsByAudience: Record<string, Actor[]>;
  tasks: Task[];
  studyCases: StudyCase[];
  reviews: Review[];
  ratings: Rating[];
  bigNumbers: BigNumber[];
}

/**
 * Options for loading source data
 */
export interface LoadOptions {
  /** Maximum number of items to load per collection (default: 50) */
  limit?: number;
}

/**
 * SourceDataLoaderService
 *
 * Loads all source data types from Firestore with detailed logging.
 * Uses dependency injection for all Firebase services.
 *
 * Usage:
 * ```typescript
 * const loader = new SourceDataLoaderService(
 *   projectId,
 *   audienceService,
 *   taskService,
 *   studyCaseService,
 *   reviewService,
 *   ratingService,
 *   bigNumberService
 * );
 *
 * const data = await loader.loadAll({ limit: 50 });
 * // {
 * //   audiences: [...],
 * //   actorsByAudience: { 'aud-1': [...], 'aud-2': [...] },
 * //   tasks: [...],
 * //   studyCases: [...],
 * //   reviews: [...],
 * //   ratings: [...],
 * //   bigNumbers: [...]
 * // }
 * ```
 */
export class SourceDataLoaderService {
  constructor(
    private readonly projectId: string,
    private readonly audienceService: AudienceService,
    private readonly taskService: TaskService,
    private readonly studyCaseService: StudyCaseService,
    private readonly reviewService: ReviewService,
    private readonly ratingService: RatingService,
    private readonly bigNumberService: BigNumberService
  ) {}

  /**
   * Load all source data types
   *
   * @param options - Loading options (limit)
   * @returns {Promise<SourceDataLoadResult>} All loaded source data
   *
   * @example
   * ```typescript
   * const data = await loader.loadAll({ limit: 50 });
   * console.log('Loaded', data.audiences.length, 'audiences');
   * ```
   */
  async loadAll(options: LoadOptions = {}): Promise<SourceDataLoadResult> {
    const limit = options.limit || 50;
    const startTime = Date.now();

    console.log(`🚀 [SourceDataLoader] Starting PARALLEL data load for project: ${this.projectId}`);

    try {
      // ✅ PARALLEL LOADING: Load all top-level collections simultaneously
      const [audiences, tasks, studyCases, reviews, ratings, bigNumbers] = await Promise.all([
        this.audienceService.list({ limit }),
        this.taskService.list({ limit }),
        this.studyCaseService.list({ limit }),
        this.reviewService.list({ limit }),
        this.ratingService.list({ limit }),
        this.bigNumberService.list({ limit }),
      ]);

      console.log(`👥 [SourceDataLoader] Loaded ${audiences.length} audiences`);
      console.log(`📋 [SourceDataLoader] Loaded ${tasks.length} tasks`);
      console.log(`📚 [SourceDataLoader] Loaded ${studyCases.length} study cases`);
      console.log(`⭐ [SourceDataLoader] Loaded ${reviews.length} reviews`);
      console.log(`📊 [SourceDataLoader] Loaded ${ratings.length} ratings`);
      console.log(`🔢 [SourceDataLoader] Loaded ${bigNumbers.length} big numbers`);

      // ✅ PARALLEL LOADING: Load actors for all audiences simultaneously
      const actorPromises = audiences
        .filter(a => a.id)
        .map(async (audience) => {
          const actorService = this.audienceService.getActorsService(audience.id!);
          const actors = await actorService.list({ limit });
          return { audienceId: audience.id!, actors };
        });

      const actorsResults = await Promise.all(actorPromises);

      const actorsByAudience: Record<string, Actor[]> = {};
      let totalActors = 0;

      for (const { audienceId, actors } of actorsResults) {
        actorsByAudience[audienceId] = actors;
        totalActors += actors.length;
      }

      console.log(`🎭 [SourceDataLoader] Loaded ${totalActors} actors across ${audiences.length} audiences`);

      const elapsed = Date.now() - startTime;
      const totalItems = audiences.length + totalActors + tasks.length + studyCases.length +
                        reviews.length + ratings.length + bigNumbers.length;

      console.log(`✅ [SourceDataLoader] All data loaded successfully in ${elapsed}ms (PARALLEL)`);
      console.log(`📦 [SourceDataLoader] Summary: ${totalItems} total items (${audiences.length} audiences, ${totalActors} actors, ${tasks.length} tasks, ${studyCases.length} study cases, ${reviews.length} reviews, ${ratings.length} ratings, ${bigNumbers.length} big numbers)`);

      return {
        audiences,
        actorsByAudience,
        tasks,
        studyCases,
        reviews,
        ratings,
        bigNumbers,
      };
    } catch (error) {
      console.error('❌ [SourceDataLoader] Failed to load source data:', error);
      throw error;
    }
  }
}
